import { useEffect, useRef } from "react";
import { useFrame, useThree } from "@react-three/fiber";
import { glide, portal, portalTo, pose } from "./Player";
import { perf } from "../../lib/perf";
import { sectionOf } from "../../lib/sections";
import { FEEL, regionAt } from "../../lib/world";
import { RAINBOW_ORDER } from "../../lib/sections";
import { audio } from "../../lib/audio";

const EXPO = { hall: 1, love: 1, nature: 1.04, spirit: 1, thoughts: 1, fire: 1.06 };

export const hallInfo = { section: null, near: null, weights: [], lite: false };
const forcedLite = new URLSearchParams(window.location.search).get("hall") === "lite";

export function HallDirector({ paintings, slots, onNear, onWalkIn, onMusic, onLite, onRegion }) {
  const { setDpr, gl } = useThree();
  const st = useRef({ near: null, armed: {}, target: null, since: 0, sent: null, lowT: 0, lite: false });
  useEffect(() => {
    window.__afaeMusicResync = () => (st.current.sent = null);
    return () => delete window.__afaeMusicResync;
  }, []);

  useFrame((_, dt) => {
    const s = st.current;
    const now = performance.now() / 1000;
    const fx = -Math.sin(pose.yaw);
    const fz = -Math.cos(pose.yaw);
    let near = null;
    let best = -Infinity;
    const prev = s.prevD || (s.prevD = {});
    const region = regionAt(pose.x, pose.z);
    if (region !== s.region) {
      if (s.region && region === "spirit") audio.bell?.();
      s.region = region;
      onRegion?.(region);
    }
    const atArch = region === "hall" && !glide.on && !portal.on && pose.z < FEEL.z + 0.8 && fz < -0.5 && RAINBOW_ORDER.find((k) => Math.abs(pose.x - FEEL.xs[k]) < FEEL.w / 2 - 0.25);
    if (atArch && !s.portalHeld) portalTo(atArch);
    s.portalHeld = !!atArch;
    gl.toneMappingExposure += ((EXPO[region] || 1) - gl.toneMappingExposure) * Math.min(1, dt * 1.5);
    for (const p of paintings) {
      const sl = slots[p.id];
      if (!sl || sl.section !== region) continue;
      const dx = sl.x - pose.x;
      const dz = sl.z - pose.z;
      const d = Math.hypot(dx, dz) || 0.001;
      const facing = (dx * fx + dz * fz) / d;
      const approach = prev[p.id] === undefined ? 0 : (prev[p.id] - d) / Math.max(dt, 1e-3);
      prev[p.id] = d;
      if (d > 2.0) s.armed[p.id] = true;
      if (!glide.on && !portal.on && d < 1.35 && s.armed[p.id] && facing > 0.6 && approach > 0.15) {
        s.armed[p.id] = false;
        onWalkIn(p);
        return;
      }
      const score = facing * 1.6 - d * 0.25;
      if (d < 3.8 && facing > 0.35 && score > best) {
        best = score;
        near = p;
      }
    }
    if ((near?.id || null) !== s.near) {
      s.near = near?.id || null;
      onNear(s.near);
    }
    const sec = near ? sectionOf(near) : region !== "hall" ? region : null;
    Object.assign(hallInfo, { section: sec, region, near: near?.slug || null, weights: [] });
    // rooms keep their one room track while paintings step by; a painting's own track plays when it is opened
    const target = region !== "hall" ? `section:${region}` : near ? near.slug : sec ? `section:${sec}` : "hall";
    if (target !== s.target) {
      s.target = target;
      s.since = now;
    } else if (s.sent !== target && now - s.since > 0.45) {
      s.sent = target;
      onMusic(target, region === "hall" ? near : null, sec);
    }
    s.lowT = perf.fps < 26 ? s.lowT + dt : Math.max(0, s.lowT - dt);
    if (!s.lite && (forcedLite || s.lowT > 3)) {
      s.lite = hallInfo.lite = true;
      setDpr(1);
      onLite();
    }
  });
  return null;
}
